import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { videos, categories } from '@/mocks/videos';

const categoryNameMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
};

export default function ArticlePage() {
  const params = useParams();
  const { id } = params;
  const videoId = parseInt(id || '1', 10);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const video = videos.find((v) => v.id === videoId);

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <i className="ri-error-warning-line text-6xl text-foreground-300"></i>
            <h2 className="mt-6 text-2xl font-heading font-bold text-foreground-900">视频未找到</h2>
            <p className="mt-2 text-foreground-500">该视频可能已下架或链接失效</p>
            <a href="/" className="inline-block mt-6 px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">返回首页</a>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryName = categoryNameMap[video.category] || video.category;
  const relatedVideos = videos
    .filter((v) => v.category === video.category && v.id !== video.id)
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${video.category}` },
    { label: video.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    datePublished: `${video.year}-01-01`,
    author: { '@type': 'Organization', name: '51视频' },
    publisher: { '@type': 'Organization', name: '51视频' },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://51video.example.com/article/${video.id}` },
  };

  return (
    <>
      <SEOHead
        title={`${video.title} - 51视频 | ${video.title}${video.type}免费在线观看高清`}
        description={`${video.title}，${video.type}，${video.region}${video.year}年上映。${video.description.substring(0, 100)}... 51视频提供高清免费在线观看，不卡顿不缓冲。`}
        keywords={`${video.title},${video.type},${video.region},免费在线观看,高清,51视频,在线播放`}
        canonicalUrl={`https://51video.example.com/article/${video.id}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51video.example.com/' },
          { '@type': 'ListItem', position: 2, name: categoryName, item: `https://51video.example.com/category/${video.category}` },
          { '@type': 'ListItem', position: 3, name: video.title, item: `https://51video.example.com/article/${video.id}` },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-5xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <article className="pb-14">
                {/* Video Player Area */}
                <div className="relative aspect-video bg-black rounded-lg overflow-hidden mb-6">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={video.cover}
                      alt={video.title}
                      title={`51视频 - ${video.title}`}
                      className="w-full h-full object-cover opacity-60"
                      width="1280"
                      height="720"
                    />
                    <div className="absolute inset-0 bg-black/30"></div>
                    <button className="relative z-10 w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 transition-colors cursor-pointer">
                      <i className="ri-play-fill text-white text-4xl ml-1"></i>
                    </button>
                  </div>
                </div>

                {/* Title & Meta */}
                <header className="mb-6">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                    {video.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-star-fill text-accent-500"></i>
                      <strong className="text-accent-500 font-semibold">{video.rating}</strong>
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="whitespace-nowrap">{video.year}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="whitespace-nowrap">{video.region}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="whitespace-nowrap">{video.type}</span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-primary-500 font-medium whitespace-nowrap">{video.updateInfo}</span>
                  </div>
                </header>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-3 mb-8">
                  <button className="flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-play-fill text-lg"></i>立即播放
                  </button>
                  <button className="flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line text-lg"></i>收藏
                  </button>
                  <button className="flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line text-lg"></i>分享
                  </button>
                </div>

                {/* Description */}
                <section className="mb-10" aria-label="视频简介">
                  <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-3">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">{video.description}</p>
                </section>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-10">
                  {video.type.split(' / ').map((t) => (
                    <span key={t} className="px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs font-medium whitespace-nowrap">{t}</span>
                  ))}
                  <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap">{video.region}</span>
                  <span className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap">{video.year}</span>
                </div>

                {/* Related Videos */}
                {relatedVideos.length > 0 && (
                  <section aria-label="相关推荐">
                    <h2 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                      <i className="ri-links-line text-primary-500 mr-2"></i>
                      相关推荐
                    </h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {relatedVideos.map((v) => (
                        <VideoCard
                          key={v.id}
                          id={v.id}
                          title={v.title}
                          cover={v.cover}
                          rating={v.rating}
                          category={v.category}
                          updateInfo={v.updateInfo}
                          categoryName={categoryNameMap[v.category] || v.category}
                          isHot={v.isHot}
                          isNew={v.isNew}
                        />
                      ))}
                    </div>
                  </section>
                )}
              </article>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}